/** @format */

import { Fragment, useState, type ReactNode } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { HomeIcon } from './icons';
import { Link, useNavigate } from 'react-router-dom';
import { CustomDropDown } from 'src/components/CustomDropDown/CustomDropDown';

type BreadcrumbLink =
  | string
  | {
      pathname: string;
      search?: string;
      state?: Record<string, unknown>;
    };

type BreadcrumbOption = {
  title?: string;
  link?: BreadcrumbLink;
};

type BreadcrumbDataItem = {
  title?: ReactNode;
  link?: BreadcrumbLink;
  options?: BreadcrumbOption[];
};

type BreadcrumbProps = {
  data?: BreadcrumbDataItem[];
  additionalClasses?: string;
};

const Breadcrumb = ({ data, additionalClasses }: BreadcrumbProps) => {
  const navigate = useNavigate();

  const BreadcrumbItem = ({
    title,
    link,
    currentPage
  }: {
    title?: ReactNode;
    link?: BreadcrumbLink;
    currentPage?: boolean;
  }) => (
    <>
      <span className="breadcrumbItemSeparator">/</span>
      <span className={currentPage ? 'breadcrumbItemSeparator' : 'breadcrumbItemText'}>
        {link ? <Link to={link}>{title}</Link> : title}
      </span>
    </>
  );

  const BreadcrumbItemDropdown = ({
    title,
    options,
    currentPage,
    breadcrumbKey
  }: {
    title?: ReactNode;
    options?: BreadcrumbOption[];
    currentPage?: boolean;
    breadcrumbKey?: number;
  }) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleClose = () => {
      setIsOpen(false);
    };
    return (
      <>
        <span className="breadcrumbItemSeparator">/</span>
        <CustomDropDown
          placement="bottomLeft"
          open={isOpen}
          onOpenChange={setIsOpen}
          overlayClassName="breadcrumb-dropdown-menu"
          trigger={
            <div
              aria-controls={`breadcrumb_dropdown_${breadcrumbKey}`}
              className="breadcrumbItemDropdownBtn"
              aria-haspopup="true">
              <span
                className={
                  currentPage
                    ? 'breadcrumbItemSeparator breadcrumbItemDropdownBtnSpan'
                    : 'breadcrumbItemText breadcrumbItemDropdownBtnSpan'
                }>
                {title} <ArrowDropDownIcon />
              </span>
            </div>
          }>
          <ul className="custom-dropdown__menu" id={`breadcrumb_dropdown_${breadcrumbKey}`} role="menu">
            {options?.map((item, index) => {
              return (
                <li className="custom-dropdown__menu-item" key={index} role="none">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      handleClose();
                      navigate(item?.link || '/');
                    }}>
                    {item?.title}
                  </button>
                </li>
              );
            })}
          </ul>
        </CustomDropDown>
      </>
    );
  };

  return (
    <div className={`breadcrumbWrapper container ${additionalClasses || ''}`}>
      <Link to="/dashboard">
        <HomeIcon />
      </Link>

      {data?.map((item, index) => {
        if (item?.options && item?.options?.length) {
          return (
            <Fragment key={index}>
              <BreadcrumbItemDropdown
                breadcrumbKey={index}
                title={item?.title}
                options={item?.options}
                currentPage={Boolean(data?.length - 1 === index)}
              />
            </Fragment>
          );
        }

        return (
          <Fragment key={index}>
            <BreadcrumbItem
              key={index}
              title={item?.title}
              link={item?.link}
              currentPage={Boolean(data?.length - 1 === index)}
            />
          </Fragment>
        );
      })}
    </div>
  );
};

export default Breadcrumb;
